import ax from "axios";
import { store } from "@/redux/store";
import { authActions } from "@/redux/reducers/auth";
import { FORBIDDEN_EVENT } from "@/hooks/useAdminAccess";


const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const api = ax.create({
    baseURL: API_BASE_URL,
    withCredentials: true,
    timeout: 30000,
    headers: {
        "Content-Type": "application/json",
    },
});

// Request interceptor - Add authorization header
api.interceptors.request.use(
    (config) => {
        const fullUrl = (config.baseURL || '') + (config.url || '');
        const isPublic = fullUrl.includes(API_BASE_URL + '/public/');
        if (!isPublic) {
            const token = localStorage.getItem("access_token");
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Response interceptor - Handle 401 and token refresh
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // If 401 error and not already retried
        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            const authType = localStorage.getItem("auth_type");
            const refreshToken = localStorage.getItem("refresh_token");

            // Only attempt refresh for local auth with refresh token
            if (authType === "local" && refreshToken) {
                try {
                    const response = await ax.post(`${API_BASE_URL}/auth/refresh`, {
                        refreshToken,
                    });

                    const { accessToken } = response.data;
                    localStorage.setItem("access_token", accessToken);

                    // Retry original request with new token
                    originalRequest.headers.Authorization = `Bearer ${accessToken}`;
                    return api(originalRequest);
                } catch (refreshError) {
                    console.error("Token refresh failed:", refreshError);
                    // Clear tokens and redirect to login
                    localStorage.removeItem("access_token");
                    localStorage.removeItem("refresh_token");
                    localStorage.removeItem("auth_type");
                    localStorage.removeItem("user");
                    store.dispatch(authActions.logout());
                    window.location.href = "/login";
                    return Promise.reject(refreshError);
                }
            } else {
                // For Auth0 or no refresh token, just logout
                localStorage.removeItem("access_token");
                localStorage.removeItem("refresh_token");
                localStorage.removeItem("auth_type");
                localStorage.removeItem("user");
                store.dispatch(authActions.logout());
                window.location.href = "/login";
            }
        }
// Permissions may have changed mid-session; /me itself is excluded to avoid a loop
        if (error.response?.status === 403 && !originalRequest?.url?.includes("/admin/admins/me")) {
            window.dispatchEvent(new Event(FORBIDDEN_EVENT));
        }
        return Promise.reject(error);
    }
);

export const axiosInstance = api;
export default api;
export { API_BASE_URL };